import React, { useEffect, useState } from 'react';
import { Button, Input, message } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { formatNumeroCotizacion, isNumeroCotizacionValido } from './numeroCotizacionUtils';

const getProductosDeCotizacion = (c) => {
  if (Array.isArray(c.productos) && c.productos.length > 0) return c.productos;
  return [
    {
      productoId: c.productoId,
      productoNombre: c.productoNombre,
      posicionArancelaria: c.posicionArancelaria,
      conceptosGrilla: c.conceptosGrilla || [],
    },
  ];
};

/** Celda que agrupa filas de la misma cotización (rowSpan). */
export const renderCeldaAgrupada = (row, content) => {
  if (row._esFilasProductoSecundario) {
    return { props: { rowSpan: 0 } };
  }
  return {
    children: content,
    props: { rowSpan: row._rowSpanCotizacion },
  };
};

/** Filas visibles: 1 por cotización colapsada; N si está expandida (alineación nativa de tabla). */
export const buildFilasGrillaCotizaciones = (cotizaciones, expandedIds) => {
  const expanded = expandedIds instanceof Set ? expandedIds : new Set(expandedIds || []);
  const out = [];

  for (const c of cotizaciones || []) {
    const prods = getProductosDeCotizacion(c);
    const isExpanded = expanded.has(c.id);
    const visibleProds = isExpanded ? prods : [prods[0]];
    const rowSpan = isExpanded ? prods.length : 1;

    visibleProds.forEach((p, idx) => {
      out.push({
        key: `${c.id}-p-${p.productoId ?? idx}`,
        ...c,
        productoNombre: p.productoNombre,
        posicionArancelaria: p.posicionArancelaria,
        conceptosGrilla: p.conceptosGrilla || [],
        _esFilasProductoSecundario: idx > 0,
        _rowSpanCotizacion: idx === 0 ? rowSpan : 0,
        _totalProductos: prods.length,
        _productoIndex: idx,
      });
    });
  }

  return out;
};

const NumeroSecuencialEditable = ({ valor, row, onGuardar }) => {
  const inicial = formatNumeroCotizacion(valor);
  const [local, setLocal] = useState(inicial);

  useEffect(() => {
    setLocal(formatNumeroCotizacion(valor));
  }, [valor, row.id]);

  const commit = () => {
    const raw = String(local ?? '').trim();
    if (!isNumeroCotizacionValido(raw)) {
      message.warning('El numero debe ser solo digitos (ej: 001, 023)');
      setLocal(formatNumeroCotizacion(valor));
      return;
    }
    const next = formatNumeroCotizacion(raw);
    const prev = formatNumeroCotizacion(valor);
    if (next === prev) {
      setLocal(next);
      return;
    }
    void onGuardar(row, next);
  };

  return (
    <Input
      size="small"
      value={local}
      style={{ width: 72 }}
      maxLength={20}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d]/g, '');
        setLocal(v);
      }}
      onPressEnter={(e) => {
        e.target?.blur?.();
      }}
      onBlur={commit}
    />
  );
};

/** Columna Nº con botón +/- para expandir productos adicionales.
 * Si `onGuardarNumero` está definido, el número es editable. */
export const renderColumnaNumeroConExpand = (v, row, onToggleExpand, onGuardarNumero) => {
  if (row._esFilasProductoSecundario) {
    return { props: { rowSpan: 0 } };
  }
  const nro = formatNumeroCotizacion(v ?? row.id) || '—';
  const expandBtn =
    row._totalProductos > 1 ? (
      <Button
        type="text"
        size="small"
        icon={row._rowSpanCotizacion > 1 ? <MinusOutlined /> : <PlusOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand(row.id);
        }}
        aria-label={row._rowSpanCotizacion > 1 ? 'Ocultar productos' : 'Ver más productos'}
      />
    ) : null;

  const numeroNode = onGuardarNumero ? (
    <NumeroSecuencialEditable valor={v ?? row.id} row={row} onGuardar={onGuardarNumero} />
  ) : (
    nro
  );

  return {
    props: { rowSpan: row._rowSpanCotizacion },
    children:
      expandBtn != null ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          {expandBtn}
          {numeroNode}
        </span>
      ) : (
        numeroNode
      ),
  };
};

/** Títulos de columnas de conceptos a partir de todos los productos de la página. */
export const titulosConceptosDesdeCotizaciones = (cotizaciones) => {
  const ordenPorNombre = new Map();
  (cotizaciones || []).forEach((c) => {
    getProductosDeCotizacion(c).forEach((p) => {
      (p.conceptosGrilla || []).forEach((l) => {
        const nombre = l.nombre;
        if (!nombre) return;
        const orden = l.orden ?? 0;
        if (!ordenPorNombre.has(nombre)) ordenPorNombre.set(nombre, orden);
        else ordenPorNombre.set(nombre, Math.min(ordenPorNombre.get(nombre), orden));
      });
    });
  });
  return Array.from(ordenPorNombre.entries())
    .sort((a, b) => a[1] - b[1] || String(a[0]).localeCompare(String(b[0])))
    .map(([nombre]) => nombre);
};
